import { COLORS, OptimizationTypes } from '@app/configurations';
import {
  AdPlatform,
  CampaignOptimizationValue,
  CampaignStatus,
  CampaignType,
  GoalType,
  UserMaxBidAPIResponse,
  Warning,
} from '@app/core/models/common.models';
import { Currency } from '@app/core/models/customer.models';

import {
  AllMetrics,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  CONVERSIONS,
  COST,
  COST_PER_CLICK,
  COST_PER_CONVERSION,
  COST_PER_MILLE,
  COST_REVENUE_RATIO,
  IMPRESSIONS,
  PG_GOAL_METRIC_COST_PER_CLICK,
  PG_GOAL_METRIC_COST_PER_CONVERSION,
  PG_GOAL_METRIC_COST_PER_DAY,
  PG_GOAL_METRIC_CRR,
  PG_GOAL_METRIC_PROFIT_MAXIMIZATION,
  PG_GOAL_METRIC_ROAS,
  PG_GOAL_METRIC_ROI,
  PROFIT_GROSS,
  PROFIT_NET,
  RETURN_ON_INVESTMENT,
  REVENUE,
  ROAS,
  VIEW_IMPRESSIONS,
} from '@app/core/models/metrics.constants';

export const PGOptiTypeColors = {
  CLICK: COLORS.OPT_TYPE_TEAL,
  REVENUE: COLORS.OPT_TYPE_VIOLET,
  PROFIT: COLORS.OPT_TYPE_GREEN,
  VCPM: COLORS.OPT_TYPE_PINK,
};

export enum PGCreationSteps {
  Type = 'type',
  Campaigns = 'campaigns',
  Name = 'name',
  Goal = 'goal',
  ID = 'id', // for edit mode
}

export const PerformanceGroupGoalType = {
  CLICK: [PG_GOAL_METRIC_COST_PER_CLICK, PG_GOAL_METRIC_COST_PER_DAY],
  REVENUE: [PG_GOAL_METRIC_COST_PER_DAY, PG_GOAL_METRIC_COST_PER_CONVERSION, PG_GOAL_METRIC_CRR, PG_GOAL_METRIC_ROAS],
  PROFIT: [
    PG_GOAL_METRIC_COST_PER_DAY,
    PG_GOAL_METRIC_COST_PER_CONVERSION,
    PG_GOAL_METRIC_PROFIT_MAXIMIZATION,
    PG_GOAL_METRIC_ROI,
  ],
  VCPM: [PG_GOAL_METRIC_COST_PER_DAY, PG_GOAL_METRIC_COST_PER_CONVERSION, PG_GOAL_METRIC_CRR, PG_GOAL_METRIC_ROAS],
};
export interface SelectCampaignsListItem {
  adspertId: string | number;
  accountName: string;
  platform: AdPlatform | string;
  accountTags: string[];
  pgName: string;
  pgId?: number;
  id: string;
  adspertCampaignId: string;
  campaignName: string;
  campaignLabels: string[];
  campaignStatus: CampaignStatus;
  campaignType: CampaignType | string;
  campaignOptimizationType: CampaignOptimizationValue;
  allMetrics?: { key: AllMetrics | string; value: string | number }[];
}
export const MetricsViewKeyPrefix = 'pgCampaignSelection';
export const ManageCampaignsInPGMetricsViewKeyPrefix = 'managePgCampaignSelection';

export const ViewableMetricsForOptimizationTypes = {
  [OptimizationTypes.REVENUE]: {
    all: [
      COST,
      IMPRESSIONS,
      CLICKS,
      CONVERSIONS,
      COST_REVENUE_RATIO,
      COST_PER_CONVERSION,
      ROAS,
      REVENUE,
      CONVERSION_RATE,
    ] as Partial<AllMetrics>[],
    default: [COST, CONVERSIONS, COST_REVENUE_RATIO, ROAS] as Partial<AllMetrics>[],
    goals: {
      [PG_GOAL_METRIC_COST_PER_DAY]: [CONVERSIONS, REVENUE, COST_REVENUE_RATIO, ROAS],
      [PG_GOAL_METRIC_COST_PER_CONVERSION]: [CONVERSIONS, REVENUE, COST_REVENUE_RATIO, ROAS],
      [PG_GOAL_METRIC_CRR]: [CONVERSIONS, REVENUE, ROAS, COST],
      [PG_GOAL_METRIC_ROAS]: [CONVERSIONS, REVENUE, COST, CONVERSION_RATE],
    },
  },
  [OptimizationTypes.PROFIT]: {
    all: [
      COST,
      IMPRESSIONS,
      CLICKS,
      CONVERSIONS,
      COST_PER_CONVERSION,
      RETURN_ON_INVESTMENT,
      PROFIT_NET,
      PROFIT_GROSS,
      CONVERSION_RATE,
    ] as Partial<AllMetrics>[],
    default: [COST, PROFIT_NET, CONVERSIONS, RETURN_ON_INVESTMENT] as Partial<AllMetrics>[],
    goals: {
      [PG_GOAL_METRIC_COST_PER_DAY]: [CONVERSIONS, COST, PROFIT_NET, RETURN_ON_INVESTMENT],
      [PG_GOAL_METRIC_COST_PER_CONVERSION]: [CONVERSIONS, COST, PROFIT_NET, RETURN_ON_INVESTMENT],
      [PG_GOAL_METRIC_PROFIT_MAXIMIZATION]: [CONVERSIONS, CONVERSION_RATE, COST, RETURN_ON_INVESTMENT],
      [PG_GOAL_METRIC_ROI]: [CONVERSIONS, CONVERSION_RATE, PROFIT_NET, COST],
    },
  },
  [OptimizationTypes.CLICK]: {
    all: [COST, IMPRESSIONS, CLICKS] as Partial<AllMetrics>[],
    default: [COST, IMPRESSIONS, CLICKS] as Partial<AllMetrics>[],
    goals: {
      [PG_GOAL_METRIC_COST_PER_DAY]: [IMPRESSIONS, COST, COST_PER_CLICK, CLICK_THROUGH_RATE],
      [PG_GOAL_METRIC_COST_PER_CLICK]: [IMPRESSIONS, COST, COST_PER_CLICK, CLICK_THROUGH_RATE],
    },
  },
  [OptimizationTypes.VCPM]: {
    all: [
      COST,
      IMPRESSIONS,
      CLICKS,
      CONVERSIONS,
      COST_REVENUE_RATIO,
      COST_PER_CONVERSION,
      ROAS,
      REVENUE,
      CONVERSION_RATE,
      VIEW_IMPRESSIONS,
      COST_PER_MILLE,
    ] as Partial<AllMetrics>[],
    default: [COST, CONVERSIONS, COST_REVENUE_RATIO, ROAS] as Partial<AllMetrics>[],
    goals: {
      [PG_GOAL_METRIC_COST_PER_DAY]: [CONVERSIONS, REVENUE, COST_REVENUE_RATIO, ROAS],
      [PG_GOAL_METRIC_COST_PER_CONVERSION]: [CONVERSIONS, REVENUE, COST_REVENUE_RATIO, ROAS],
      [PG_GOAL_METRIC_CRR]: [CONVERSIONS, REVENUE, ROAS, COST],
      [PG_GOAL_METRIC_ROAS]: [CONVERSIONS, REVENUE, COST, CONVERSION_RATE],
    },
  },
};

export interface SelectedGoal {
  currency: Currency | null;
  value: number | null;
  key: GoalType;
}

export interface PGWizardState {
  [PGCreationSteps.Type]: CampaignOptimizationValue;
  [PGCreationSteps.Campaigns]: SelectCampaignsListItem[];
  [PGCreationSteps.Name]: string;
  [PGCreationSteps.Goal]: SelectedGoal[];
}

export interface PGEditInfo {
  name: string;
  currency: Currency;
  campaigns: SelectCampaignsListItem[];
  id: number;
  type: CampaignOptimizationValue;
  goals: SelectedGoal[];
  warningStatus: string;
  warnings: Warning[];
  userMaxBid?: UserMaxBidAPIResponse;
  pgCurrency?: Currency;
}

export const getPGCreationErrorTypes = (messages = []) => {
  const msgs = {
    'more-than-once': 'Campaign included more than once',
    unknown: 'Unknown campaign',
    deleted: 'Campaign is deleted',
    'cannot-optimize': 'Campaign cannot be optimized',
    'type-mismatch': 'Optimization type of the campaign does not match optimization type of the performance group',
  };
  let key = 'other';
  Object.keys(msgs).forEach((k) => {
    if (messages.includes(msgs[k])) key = k;
  });
  return key;
};

export type PerfData = Record<GoalType, number | string>;
export type HistoricData = Record<GoalType | 'date', number | string>[];

export interface PerformanceGroupGoal {
  goalType: GoalType;
  goalValue: number;
  goalCurrency: Currency;
  goalMicros: number;
  goalRatio: number;
}
export enum HintType {
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
}

export enum AddGoalComponentMode {
  MOVE = 'MOVE',
  NEW_PG = 'NEW_PG',
  WIZARD = 'WIZARD',
  QUICK_EDIT = 'QUICK_EDIT',
  CAMPAIGN_CREATION = 'CAMPAIGN_CREATION',
}

export interface GoalsSummaryModel {
  'COST-PER-DAY': number;
  'COST-PER-CLICK': any;
  'COST-PER-CONVERSION': any;
  CRR: any;
  ROI: any;
  ROAS: any;
  'PROFIT-MAXIMIZATION': number;
  ROAS_CLICKS: any;
  CRR_CLICKS: any;
  'COST-PER-CONVERSION_CLICKS': any;
}
